/**
 * Map Data Transformers
 *
 * Utilities to transform list data into GeoJSON format for map visualization.
 */

import type {
  Feature,
  FeatureCollection,
  Point,
  Polygon,
  MultiPolygon,
} from 'geojson'
import proj4 from 'proj4'
import type { Zoning } from '@popety-io/types'
import {
  orderedModes,
  bestHeadwayMin,
} from '../../../organisms/Amenities/transitDisplay'

// Define Swiss LV95 (EPSG:2056) projection
proj4.defs(
  'EPSG:2056',
  '+proj=somerc +lat_0=46.95240555555556 +lon_0=7.439583333333333 +k_0=1 +x_0=2600000 +y_0=1200000 +ellps=bessel +towgs84=674.374,15.056,405.346,0,0,0,0 +units=m +no_defs'
)

// Define WGS84 (EPSG:4326) projection
proj4.defs('EPSG:4326', '+proj=longlat +datum=WGS84 +no_defs')

/**
 * Parse WKT POINT format to coordinates
 * Example: "POINT(6.148291 46.199797)" -> [6.148291, 46.199797]
 */
function parseWKTPoint(wkt: string): [number, number] | null {
  if (!wkt || typeof wkt !== 'string') return null

  const match = wkt.match(/POINT\s*\(\s*([-\d.]+)\s+([-\d.]+)\s*\)/i)
  if (!match) return null

  const lon = parseFloat(match[1])
  const lat = parseFloat(match[2])

  if (isNaN(lon) || isNaN(lat)) return null

  return [lon, lat]
}

/**
 * Extract coordinates from geo_center field
 * Handles both object format {lat, lon} and WKT string format "POINT(lon lat)"
 */
function extractCoordinates(geo_center: any): [number, number] | null {
  if (!geo_center) return null

  // Handle object format: {lat: 46.199797, lon: 6.148291}
  if (typeof geo_center === 'object' && geo_center.lat && geo_center.lon) {
    return [geo_center.lon, geo_center.lat]
  }

  // Handle WKT string format: "POINT(6.148291 46.199797)"
  if (typeof geo_center === 'string') {
    return parseWKTPoint(geo_center)
  }

  return null
}

/**
 * Transform listings array to GeoJSON FeatureCollection
 */
export function listingsToGeoJSON(listings: any[]): FeatureCollection<Point> {
  const features: Feature<Point>[] = listings
    .map((listing) => {
      const coordinates = extractCoordinates(listing.geo_center)
      if (!coordinates) return null

      return {
        type: 'Feature' as const,
        id: listing.id,
        geometry: {
          type: 'Point' as const,
          coordinates,
        },
        properties: {
          id: listing.id,
          price: listing.price,
          priceLabel: formatPriceForMarker(listing.price), // Formatted price for marker display
          area: listing.area || listing.living_area,
          rooms: listing.rooms_nb,
          propertyType: listing.property_type,
          dealType: listing.deal_type,
          street: listing.street,
          city: listing.city_name,
          constructionYear: listing.construction_year,
          listingDate: listing.listing_timestamp,
          active: listing.active,
          // Additional data for tooltip
          title: listing.title,
          urls_image: listing.urls_image?.[0],
          hash_images: listing.hash_images?.[0],
          // Price category for color coding
          priceCategory: calculatePriceCategory(listing.price, listings),
        },
      }
    })
    .filter((feature) => feature !== null) as Feature<Point>[]

  return {
    type: 'FeatureCollection',
    features,
  }
}

/**
 * Format price for map marker display
 * Examples:
 * - 850000 → "850K"
 * - 1200000 → "1.2M"
 * - 25000 → "25K"
 * - 950 → "950"
 */
export function formatPriceForMarker(price: number): string {
  if (!price || price === 0) return 'N/A'

  if (price >= 1000000) {
    const millions = price / 1000000
    // Show one decimal if not a whole number
    return millions % 1 === 0 ? `${millions}M` : `${millions.toFixed(1)}M`
  }

  if (price >= 1000) {
    const thousands = price / 1000
    // Round to nearest thousand for cleaner display
    return thousands % 1 === 0 ? `${thousands}K` : `${Math.round(thousands)}K`
  }

  return price.toString()
}

/**
 * Calculate price category (low, medium, high) for color coding
 */
function calculatePriceCategory(
  price: number,
  allListings: any[]
): 'low' | 'medium' | 'high' {
  const prices = allListings
    .map((l) => l.price)
    .filter((p) => p != null)
    .sort((a, b) => a - b)

  if (prices.length === 0) return 'medium'

  const percentile33 = prices[Math.floor(prices.length * 0.33)]
  const percentile66 = prices[Math.floor(prices.length * 0.66)]

  if (price <= percentile33) return 'low'
  if (price <= percentile66) return 'medium'
  return 'high'
}

/**
 * Transform transactions array to GeoJSON FeatureCollection
 */
export function transactionsToGeoJSON(
  transactions: any[]
): FeatureCollection<Point> {
  const features: Feature<Point>[] = transactions
    .map((tx) => {
      let coordinates: [number, number] | null = null

      // Handle location field (GeoJSON MultiPoint format - same as building permits)
      if (tx.location) {
        if (
          tx.location.type?.toLowerCase() === 'multipoint' &&
          tx.location.coordinates &&
          Array.isArray(tx.location.coordinates) &&
          tx.location.coordinates.length > 0
        ) {
          const firstPoint = tx.location.coordinates[0]
          if (Array.isArray(firstPoint) && firstPoint.length >= 2) {
            coordinates = [firstPoint[0], firstPoint[1]]
          }
        } else {
          coordinates = extractCoordinates(tx.location)
        }
      }
      // Fallback to property_geo_center (WKT string)
      else if (tx.property_geo_center) {
        coordinates = extractCoordinates(tx.property_geo_center)
      }
      // Fallback to nested transaction_properties
      else if (tx.transaction_properties?.[0]?.land_geo_center) {
        coordinates = extractCoordinates(
          tx.transaction_properties[0].land_geo_center
        )
      }

      if (!coordinates) return null

      return {
        type: 'Feature' as const,
        id: tx.id,
        geometry: {
          type: 'Point' as const,
          coordinates,
        },
        properties: {
          id: tx.id,
          price: tx.transaction_total_price || tx.price,
          priceLabel: formatPriceForMarker(
            tx.transaction_total_price || tx.price
          ), // Formatted price for marker display
          date: tx.transaction_date || tx.date,
          propertyType: tx.property_legal_type || tx.ownership_type,
          area: tx.property_land_area || tx.transaction_area,
          city: tx.property_city_name || tx.city,
          reason: tx.transaction_step_name || tx.reason || 'Transaction',
          // Recency for color coding (more recent = brighter)
          recency: calculateRecency(tx.transaction_date || tx.date),
        },
      }
    })
    .filter((feature) => feature !== null) as Feature<Point>[]

  return {
    type: 'FeatureCollection',
    features,
  }
}

/**
 * Calculate recency score (0-1, where 1 is most recent)
 */
function calculateRecency(dateString: string): number {
  if (!dateString) return 0

  const date = new Date(dateString)
  const now = new Date()
  const yearAgo = new Date()
  yearAgo.setFullYear(yearAgo.getFullYear() - 1)

  const timeSinceTransaction = now.getTime() - date.getTime()
  const yearInMs = now.getTime() - yearAgo.getTime()

  // Normalize to 0-1, where 1 is most recent
  const recency = Math.max(0, 1 - timeSinceTransaction / (yearInMs * 3))
  return Math.min(1, recency)
}

/**
 * Transform amenities data to GeoJSON by category
 */
export function amenitiesToGeoJSON(
  amenitiesData: any,
  category: string
): FeatureCollection<Point> {
  if (!amenitiesData?.category_scores) {
    return { type: 'FeatureCollection', features: [] }
  }

  const categoryData = amenitiesData.category_scores.find(
    (cat: any) => cat.category === category
  )

  if (!categoryData?.pois) {
    return { type: 'FeatureCollection', features: [] }
  }

  const features: Feature<Point>[] = categoryData.pois
    .filter((poi: any) => poi.lat && poi.lon)
    .map((poi: any, index: number) => ({
      type: 'Feature',
      id: poi.id || `${category}-${index}`,
      geometry: {
        type: 'Point',
        coordinates: [poi.lon, poi.lat],
      },
      properties: {
        id: poi.id || `${category}-${index}`,
        name: poi.name,
        category: category,
        type: poi.type,
        distance: poi.distance,
        address: poi.address,
      },
    }))

  return {
    type: 'FeatureCollection',
    features,
  }
}

/**
 * Transform amenity POIs array to GeoJSON FeatureCollection
 * Handles POIs that already have category included
 */
export function amenityPOIsToGeoJSON(
  amenityPOIs: any[]
): FeatureCollection<Point> {
  const features: Feature<Point>[] = amenityPOIs
    .map((poi) => {
      // Extract coordinates - handle both formats
      let coordinates: [number, number] | null = null

      if (poi.coordinates) {
        // Handle {latitude, longitude} format
        if (poi.coordinates.latitude && poi.coordinates.longitude) {
          coordinates = [poi.coordinates.longitude, poi.coordinates.latitude]
        }
        // Handle {lat, lon} format
        else if (poi.coordinates.lat && poi.coordinates.lon) {
          coordinates = [poi.coordinates.lon, poi.coordinates.lat]
        }
      }

      // Fallback: compute centroid from geo_center or geo_polygon
      if (!coordinates) {
        if (poi.geo_center?.coordinates) {
          coordinates = [
            poi.geo_center.coordinates[0],
            poi.geo_center.coordinates[1],
          ]
        } else if (poi.geo_polygon?.coordinates?.[0]) {
          const ring = poi.geo_polygon.coordinates[0]
          const len = ring.length
          if (len > 0) {
            let sumLon = 0
            let sumLat = 0
            for (let i = 0; i < len; i++) {
              sumLon += ring[i][0]
              sumLat += ring[i][1]
            }
            coordinates = [sumLon / len, sumLat / len]
          }
        }
      }

      if (!coordinates) return null

      return {
        type: 'Feature' as const,
        id: poi.id,
        geometry: {
          type: 'Point' as const,
          coordinates,
        },
        properties: {
          id: poi.id,
          name: poi.name,
          category: poi.category,
          subCategory: poi.sub_category,
          type: poi.type,
          distance: poi.distance,
          address: poi.address,
          rating: poi.rating,
          openNow: poi.open_now,
          photoUrl: poi.photo_url,
          /*
            Transport amenities carry every mode calling at the stop, so the
            popup can read "Tram · Bus" instead of reducing the stop to its
            single poi_type ("Tram Stop"). Empty for every non-transport POI,
            which has no `transit` at all.

            A STRING, not an array: Mapbox GL carries only primitives through
            its rendering pipeline, so an array arrives as a JSON string and no
            Array.isArray check ever matches.
          */
          modes: transitModeCodes(poi).join(','),
          headwayMin: bestHeadwayMin(poi.transit) ?? null,
        },
      }
    })
    .filter((feature) => feature !== null) as Feature<Point>[]

  return {
    type: 'FeatureCollection',
    features,
  }
}

/**
 * ATLAS mode code -> short label. Kept as a local plain-English map rather
 * than i18n: this transformer has no translation context, and the map popup
 * that reads its properties (useDynamicLayers.ts) already renders untranslated
 * English strings ("Bus", "Tram", "away", ...).
 */
const TRANSIT_MODE_LABELS: Record<string, string> = {
  TRAIN: 'Train',
  METRO: 'Metro',
  TRAM: 'Tram',
  BUS: 'Bus',
  BOAT: 'Boat',
  CABLE_CAR: 'Cable Car',
  CABLE_RAILWAY: 'Funicular',
  RACK_RAILWAY: 'Rack Railway',
  CHAIRLIFT: 'Chairlift',
}

// poi-v2's poi_type carries only a stop's highest mode (a bus+tram interchange
// is typed atlas_tram_stop), so this is only the fallback when transit.modes
// is absent.
const POI_TYPE_TRANSIT_LABELS: Record<string, string> = {
  atlas_train_station: 'Train',
  atlas_metro_station: 'Metro',
  atlas_tram_stop: 'Tram',
  atlas_bus_stop: 'Bus',
  atlas_boat_stop: 'Boat',
  atlas_cable_car_stop: 'Cable Car',
}

/**
 * Humanised transport mode for a poi-v2 stop, e.g. "Bus & Tram". Never falls
 * back to the raw poi_type/mode slug (e.g. "atlas_bus_stop") - an unmapped
 * value resolves to undefined so a popup can omit the line instead.
 */
function transitModeLabel(stop: any): string | undefined {
  const modes: string[] = Array.isArray(stop?.transit?.modes)
    ? stop.transit.modes
    : []
  const labels = modes
    .map((mode) => TRANSIT_MODE_LABELS[mode])
    .filter((label): label is string => Boolean(label))

  if (labels.length > 0) return labels.join(' & ')

  return POI_TYPE_TRANSIT_LABELS[stop?.poi_type]
}

/**
 * Every mode a stop serves, humanised and best-first (e.g. ["Tram", "Bus"]
 * for a tram+bus interchange) - the same ranking `orderedModes` gives the
 * nearby-stops tab row, so a popup and its row never disagree on order.
 * Always an array, never undefined: the map pin's paint expression reads its
 * length to decide interchange styling, and a missing property there would
 * be a runtime type error rather than a friendly fallback.
 */
const POI_TYPE_MODE_CODES: Record<string, string> = {
  atlas_train_station: 'TRAIN',
  atlas_metro_station: 'METRO',
  atlas_tram_stop: 'TRAM',
  atlas_bus_stop: 'BUS',
  atlas_boat_stop: 'BOAT',
  atlas_cable_car_stop: 'CABLE_RAILWAY',
}

/*
  Accepts both shapes this file sees: a poi-v2 row (`poi_type`) and an
  AmenityPOI (`type`). A non-transport value like 'pharmacy' simply misses the
  map and yields [], so it is safe to check both.
*/
function transitModeCodes(stop: any): string[] {
  const ranked = orderedModes(stop?.transit)
  if (ranked.length > 0) return ranked

  const fallback = POI_TYPE_MODE_CODES[stop?.poi_type ?? stop?.type]
  return fallback ? [fallback] : []
}

/**
 * Transform transport stops to GeoJSON
 * Handles poi-v2 (ES) public transport stops - the authoritative source since
 * the migration off swisstopo - plus the legacy Swiss Geo Admin shape, which
 * can still arrive in share payloads persisted before the migration.
 */
export function transportStopsToGeoJSON(
  stops: any[],
  stopType: 'bus' | 'tram' | 'train'
): FeatureCollection<Point> {
  const features: Feature<Point>[] = stops
    .map((stop, index) => {
      // Extract coordinates from geometry
      let coordinates: [number, number] | null = null

      // poi-v2 format: { geo_center: { type: 'Point', coordinates: [lon, lat] } }
      if (stop.geo_center?.coordinates) {
        coordinates = [
          stop.geo_center.coordinates[0],
          stop.geo_center.coordinates[1],
        ]
      }
      // Handle Swiss geo.admin.ch format with LV95 coordinates (legacy)
      else if (
        stop.geometry?.points &&
        Array.isArray(stop.geometry.points) &&
        stop.geometry.points.length > 0
      ) {
        const [easting, northing] = stop.geometry.points[0]
        // Convert LV95 (EPSG:2056) to WGS84 (lon/lat)
        coordinates = lv95ToWgs84(easting, northing)
      } else if (stop.geometry?.coordinates) {
        coordinates = [
          stop.geometry.coordinates[0],
          stop.geometry.coordinates[1],
        ]
      } else if (stop.lon && stop.lat) {
        coordinates = [stop.lon, stop.lat]
      }

      if (!coordinates) return null

      return {
        type: 'Feature' as const,
        id: stop.featureId || stop.id || `${stopType}-${index}`,
        geometry: {
          type: 'Point' as const,
          coordinates,
        },
        properties: {
          id: stop.featureId || stop.id || `${stopType}-${index}`,
          name: stop.name || stop.attributes?.name || 'Unknown Stop',
          stopType: stopType,
          distance: stop.distance,
          // No operator field exists on a poi-v2 row - this stays undefined
          // there and is simply omitted by the popup, rather than showing a
          // fabricated "Unknown Operator".
          operator: stop.attributes?.tuabkuerzung || stop.attributes?.operator,
          transportMode:
            transitModeLabel(stop) || stop.attributes?.verkehrsmittel_de,
          /*
            Raw ATLAS mode codes, comma-joined, so the popup can render
            "Tram · Bus" for an interchange and translate each mode itself.

            A STRING, not an array, on purpose: Mapbox GL carries only
            primitives through its rendering pipeline, so an array property
            comes back from feature.properties as a JSON string. The popup's
            Array.isArray() check therefore never matched, and it silently fell
            back to naming the single layer the pin was drawn in.

            Codes rather than English labels, also on purpose: the popup
            translates them via the amenities `transitModes.*` keys, so both
            tabs read the same words in the user's language.
          */
          modes: transitModeCodes(stop).join(','),
          // The pin's interchange colour keys off this, not off `modes.length`
          // - see the paint expression in useDynamicLayers.ts.
          modeCount: transitModeCodes(stop).length,
          headwayMin: bestHeadwayMin(stop.transit) ?? null,
        },
      }
    })
    .filter((feature) => feature !== null) as Feature<Point>[]

  return {
    type: 'FeatureCollection',
    features,
  }
}

/**
 * Convert Swiss LV95 (EPSG:2056) coordinates to WGS84 (lon/lat)
 * Approximate conversion using the official transformation formulas
 */
function lv95ToWgs84(easting: number, northing: number): [number, number] {
  // Convert to auxiliary values (in units of 1,000,000m)
  const y = (easting - 2600000) / 1000000
  const x = (northing - 1200000) / 1000000

  // Calculate longitude (λ) in seconds
  const lon =
    2.6779094 +
    4.728982 * y +
    0.791484 * y * x +
    0.1306 * y * x * x -
    0.0436 * y * y * y

  // Calculate latitude (φ) in seconds
  const lat =
    16.9023892 +
    3.238272 * x -
    0.270978 * y * y -
    0.002528 * x * x -
    0.0447 * y * y * x -
    0.014 * x * x * x

  // Convert from seconds to degrees
  const longitude = (lon * 100) / 36
  const latitude = (lat * 100) / 36

  return [longitude, latitude]
}

/**
 * Transform isochrones to GeoJSON
 * Converts isochrone data for a specific travel mode into multi-polygon GeoJSON
 * Each feature represents one time zone (5, 10, 15, or 30 minutes)
 */
export function isochronesToGeoJSON(
  isochroneData: any,
  travelMode: 'walking' | 'cycling' | 'driving'
): FeatureCollection<Polygon> {
  if (!isochroneData || !isochroneData[travelMode]) {
    return { type: 'FeatureCollection', features: [] }
  }

  const modeData = isochroneData[travelMode]

  // Check if modeData is already a GeoJSON FeatureCollection
  if (
    modeData.type === 'FeatureCollection' &&
    Array.isArray(modeData.features)
  ) {
    const features: Feature<Polygon>[] = modeData.features.map(
      (feature: any) => {
        const contourMinutes = feature.properties?.contour || 0

        return {
          type: 'Feature',
          id: `${travelMode}-${contourMinutes}min`,
          geometry: {
            type: 'Polygon',
            coordinates: feature.geometry.coordinates,
          },
          properties: {
            mode: travelMode,
            minutes: contourMinutes,
            color: getIsochroneColor(contourMinutes),
          },
        }
      }
    )

    return {
      type: 'FeatureCollection',
      features,
    }
  }

  // Legacy format: Handle object with time zone keys
  const features: Feature<Polygon>[] = []
  const timeZones = [5, 10, 15, 30]

  timeZones.forEach((minutes) => {
    const zoneData = modeData[`${minutes}min`] || modeData[minutes]

    if (!zoneData) {
      return
    }

    // Handle GeoJSON polygon or coordinates array
    let coordinates: number[][][] | null = null

    if (zoneData.type === 'Feature' && zoneData.geometry?.coordinates) {
      coordinates = zoneData.geometry.coordinates
    } else if (zoneData.geometry?.coordinates) {
      coordinates = zoneData.geometry.coordinates
    } else if (Array.isArray(zoneData.coordinates)) {
      coordinates = zoneData.coordinates
    } else if (zoneData.contours && Array.isArray(zoneData.contours)) {
      // Handle isochrone API format with contours array
      coordinates = zoneData.contours.map(
        (contour: any) => contour.coordinates || contour
      )
    }

    if (!coordinates) {
      return
    }

    features.push({
      type: 'Feature',
      id: `${travelMode}-${minutes}min`,
      geometry: {
        type: 'Polygon',
        coordinates: coordinates,
      },
      properties: {
        mode: travelMode,
        minutes: minutes,
        color: getIsochroneColor(minutes),
      },
    })
  })

  return {
    type: 'FeatureCollection',
    features,
  }
}

/**
 * Get color for isochrone time zone
 */
function getIsochroneColor(minutes: number): string {
  const colorMap: Record<number, string> = {
    5: '#22c55e', // Green
    10: '#eab308', // Yellow
    15: '#f97316', // Orange
    30: '#ef4444', // Red
  }
  return colorMap[minutes] || '#808080'
}

/**
 * Transform EV charging stations to GeoJSON
 * Groups chargers by station address and adds power-based properties
 */
export function evStationsToGeoJSON(
  evStations: any[]
): FeatureCollection<Point> {
  if (!evStations || evStations.length === 0) {
    return { type: 'FeatureCollection', features: [] }
  }

  // Group chargers by station ID or address
  const stationMap = new Map<string, any>()

  evStations.forEach((station) => {
    // Handle both API formats: direct properties or attributes nested
    const id = station.id || station.attributes?.id || 'Unknown'
    const address =
      station.address || station.attributes?.address || 'Unknown Address'

    if (!stationMap.has(id)) {
      // Extract coordinates - handle multiple formats
      let coordinates: [number, number] | null = null

      // Format 1: Direct latitude/longitude properties
      if (station.latitude && station.longitude) {
        coordinates = [station.longitude, station.latitude]
      }
      // Format 2: geometry.coordinates
      else if (station.geometry?.coordinates) {
        coordinates = [
          station.geometry.coordinates[0],
          station.geometry.coordinates[1],
        ]
      }
      // Format 3: Swiss Geo Admin format - geometry.points[0]
      else if (station.geometry?.points && station.geometry.points.length > 0) {
        const point = station.geometry.points[0]
        coordinates = [point[0], point[1]]
      }

      stationMap.set(id, {
        id,
        name: station.name || station.attributes?.name,
        address,
        coordinates,
        chargers: [],
        distance: station.distance,
        operator: station.operator || station.attributes?.operator,
        accessibility:
          station.accessibility || station.attributes?.accessibility,
        isOpen24Hours:
          station.isOpen24Hours !== undefined
            ? station.isOpen24Hours
            : station.attributes?.isOpen24Hours,
        phoneNumber: station.phoneNumber || station.attributes?.phoneNumber,
        paymentOptions:
          station.paymentOptions || station.attributes?.paymentOptions,
      })
    }

    // Add charger info
    const power = station.power || station.attributes?.power
    const status = station.status || station.attributes?.status
    const plugTypes = station.plugTypes || station.attributes?.plugTypes

    stationMap.get(id).chargers.push({
      power: parseFloat(power) || 0,
      status,
      plugTypes,
    })
  })

  // Convert to GeoJSON features
  const features: Feature<Point>[] = Array.from(stationMap.values())
    .map((station, index) => {
      if (!station.coordinates) return null

      const powers = station.chargers
        .map((c: any) => c.power)
        .filter((p: number) => p > 0)
      const maxPower = powers.length > 0 ? Math.max(...powers) : 0
      const minPower = powers.length > 0 ? Math.min(...powers) : 0

      // Determine charging type
      let chargingType = 'standard'
      if (maxPower >= 50) chargingType = 'fast'
      else if (maxPower >= 22) chargingType = 'medium'

      return {
        type: 'Feature' as const,
        id: `ev-station-${index}`,
        geometry: {
          type: 'Point' as const,
          coordinates: station.coordinates,
        },
        properties: {
          id: `ev-station-${index}`,
          address: station.address,
          chargerCount: station.chargers.length,
          maxPower,
          minPower,
          chargingType, // fast, medium, standard
          distance: station.distance,
          operator: station.operator,
          accessibility: station.accessibility,
          isOpen24Hours: station.isOpen24Hours,
        },
      }
    })
    .filter((feature) => feature !== null) as Feature<Point>[]

  return {
    type: 'FeatureCollection',
    features,
  }
}

/**
 * Transform highway access points to GeoJSON
 * Handles Swiss highway exit/entrance data with line geometry
 * Converts Swiss coordinate system (EPSG:2056) to WGS84 (EPSG:4326)
 */
export function highwayAccessToGeoJSON(
  accessPoints: any[]
): FeatureCollection<Point> {
  if (!accessPoints || accessPoints.length === 0) {
    return { type: 'FeatureCollection', features: [] }
  }

  /**
   * Convert Swiss LV95 coordinates (EPSG:2056) to WGS84 (EPSG:4326)
   * Uses proj4 for accurate coordinate transformation
   */
  const convertSwissToWGS84 = (
    easting: number,
    northing: number
  ): [number, number] => {
    const [longitude, latitude] = proj4('EPSG:2056', 'EPSG:4326', [
      easting,
      northing,
    ])
    return [longitude, latitude]
  }

  const features: Feature<Point>[] = accessPoints
    .map((access, index) => {
      // Extract coordinates
      let coordinates: [number, number] | null = null

      // Handle Swiss Geo Admin line geometry format
      if (access.geometry?.paths && access.geometry.paths.length > 0) {
        const path = access.geometry.paths[0]
        if (path.length > 0) {
          // Use the first point of the path as the marker location
          const point = path[0]
          // Check if coordinates are in Swiss system (LV95)
          if (access.geometry.spatialReference?.wkid === 2056) {
            coordinates = convertSwissToWGS84(point[0], point[1])
          } else {
            coordinates = [point[0], point[1]]
          }
        }
      }
      // Fallback to direct coordinates if available
      else if (access.geometry?.coordinates) {
        coordinates = [
          access.geometry.coordinates[0],
          access.geometry.coordinates[1],
        ]
      } else if (access.lat && access.lon) {
        coordinates = [access.lon, access.lat]
      }

      if (!coordinates) return null

      return {
        type: 'Feature' as const,
        id: access.featureId || access.id || `highway-${index}`,
        geometry: {
          type: 'Point' as const,
          coordinates,
        },
        properties: {
          id: access.featureId || access.id || `highway-${index}`,
          name:
            access.attributes?.bezeichnung || access.name || 'Highway Access',
          distance: access.distance,
          roadNumber:
            access.attributes?.strassennummer || access.attributes?.roadnumber,
          exitNumber: access.attributes?.exitnumber,
          segmentName: access.attributes?.segmentname,
        },
      }
    })
    .filter((feature) => feature !== null) as Feature<Point>[]

  return {
    type: 'FeatureCollection',
    features,
  }
}

/**
 * Create a circle GeoJSON for search radius or distance rings
 */
export function createCircleGeoJSON(
  center: { lat: number; lon: number },
  radiusMeters: number,
  properties?: Record<string, any>
): FeatureCollection<Polygon> {
  const points = 64
  const coords: number[][] = []

  // Convert radius from meters to degrees (approximate)
  const radiusDegrees = radiusMeters / 111320 // meters per degree at equator

  for (let i = 0; i < points; i++) {
    const angle = (i / points) * 2 * Math.PI
    const dx = radiusDegrees * Math.cos(angle)
    const dy = radiusDegrees * Math.sin(angle)
    coords.push([center.lon + dx, center.lat + dy])
  }

  // Close the ring
  coords.push(coords[0])

  return {
    type: 'FeatureCollection',
    features: [
      {
        type: 'Feature',
        geometry: {
          type: 'Polygon',
          coordinates: [coords],
        },
        properties: properties || {},
      },
    ],
  }
}

/**
 * Transform roof data to GeoJSON
 * The data is already in GeoJSON format from the API
 */
export const roofDataToGeoJSON = (
  roofData: any
): FeatureCollection<Polygon> => {
  if (!roofData || !roofData.features) {
    return { type: 'FeatureCollection', features: [] }
  }

  // Data is already in proper GeoJSON format from backend
  return roofData
}

/**
 * Transform zoning zones to GeoJSON FeatureCollection
 * Converts zones with geo_polygon data to GeoJSON format for map visualization
 */
export function zonesToGeoJSON(
  zones: Zoning[]
): FeatureCollection<Polygon | MultiPolygon> {
  if (!zones || zones.length === 0) {
    return { type: 'FeatureCollection', features: [] }
  }

  const features: Feature<Polygon | MultiPolygon>[] = zones
    .filter((zone) => zone.geo_polygon)
    .map((zone) => {
      // Determine zone name and code based on type
      const zoneName =
        zone.lup_name || zone.lupc_name || zone.lupo_name || 'Unknown Zone'
      const zoneCode = zone.lup_id || zone.lupc_id || zone.lupo_id || ''

      return {
        type: 'Feature' as const,
        id: zone._id,
        geometry: zone.geo_polygon,
        properties: {
          id: zone._id,
          name: zoneName,
          code: zoneCode,
          // Administrative info
          municipality: zone.municipality_name,
          region: zone.region_name,
          // Status info
          status: zone.status,
          adoptionDate: zone.adoption_date,
          abrogationDate: zone.abrogation_date,
          // Building indices (if available)
          iosMin: zone.ios_min,
          iosMax: zone.ios_max,
          cosMin: zone.cos_min,
          cosMax: zone.cos_max,
          altitudeMax: zone.altitude_max,
          floorsMax: zone.floor_nb_max,
        },
      }
    })

  return {
    type: 'FeatureCollection',
    features,
  }
}

/**
 * Transform building permits array to GeoJSON FeatureCollection
 */
export function buildingPermitsToGeoJSON(
  permits: any[]
): FeatureCollection<Point> {
  const features: Feature<Point>[] = permits
    .map((permit) => {
      // Extract coordinates - handle location, coordinates string, coordinates object, and geo_center formats
      let coordinates: [number, number] | null = null

      // Try location field first (land/building analysis and estimates use this)
      if (permit.location) {
        // Handle GeoJSON MultiPoint format
        if (
          permit.location.type?.toLowerCase() === 'multipoint' &&
          permit.location.coordinates &&
          Array.isArray(permit.location.coordinates) &&
          permit.location.coordinates.length > 0
        ) {
          // Take the first point from the multipoint
          const firstPoint = permit.location.coordinates[0]
          if (Array.isArray(firstPoint) && firstPoint.length >= 2) {
            coordinates = [firstPoint[0], firstPoint[1]]
          }
        }
        // Handle WKT or object format
        else {
          coordinates = extractCoordinates(permit.location)
        }
      }
      // Try coordinates field - can be string "lon,lat" or object {lat, lon}
      else if (permit.coordinates) {
        // If coordinates is a string like "6.15251433,46.19673935"
        if (typeof permit.coordinates === 'string') {
          const [lon, lat] = permit.coordinates.split(',').map(Number)
          if (!isNaN(lon) && !isNaN(lat)) {
            coordinates = [lon, lat]
          }
        }
        // If coordinates is an object with lat and lon
        else if (permit.coordinates.lat && permit.coordinates.lon) {
          coordinates = [permit.coordinates.lon, permit.coordinates.lat]
        }
      }
      // Fallback to geo_center
      else if (permit.geo_center) {
        coordinates = extractCoordinates(permit.geo_center)
      }

      if (!coordinates) return null

      return {
        type: 'Feature' as const,
        id: permit.id,
        geometry: {
          type: 'Point' as const,
          coordinates,
        },
        properties: {
          id: permit.id,
          primaryOfficialId: permit.primary_official_id,
          faoId: permit.fao_id,
          description: permit.description,
          cityName: permit.city_name,
          postalCodes: permit.postal_codes_names,
          inquiryStartDate: permit.inquiry_start_date,
          status: permit.latest_status_db,
          category: permit.regbl_classification_general,
          // Additional fields that might be useful
          secondaryOfficialId: permit.secondary_official_id,
          municipalityName: permit.municipality_name,
        },
      }
    })
    .filter((feature) => feature !== null) as Feature<Point>[]

  return {
    type: 'FeatureCollection',
    features,
  }
}
